import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useTranslation } from '@/i18n';
import { colors } from '@/assets/styles/colors';
import { ConfirmModal } from '@/components/ui/modals/ConfirmModal';
import { Card } from '@/components/ui/cards/Card';
import { Text } from '@/components/ui/text/Text';
import { PrimaryButton } from '@/components/ui/buttons/PrimaryButton';
import { sendMonthlyCutsToPayrollSchema } from '@/schemas/attendance';
import type {
  MonthlyCutManagementItem,
  PayrollSubmissionOutcome,
  PayrollSubmissionSummary,
} from '@/api/domains/attendance';
import { useSendToPayroll } from '@/hooks/attendance/monthlyCuts/useSendToPayroll';


interface SendMonthlyCutsToPayrollModalProps {
  isOpen: boolean;
  // Cortes seleccionados (fila o selección masiva). Se muestran en la confirmación y se envían
  // todos: el backend clasifica los cerrados como omitidos en el resultado.
  selectedCuts: MonthlyCutManagementItem[];
  onClose: () => void;
  // Efectos posteriores al envío (limpiar selección + refrescar listado). No cierra el diálogo: la
  // fase de resultado permanece visible hasta que el usuario la cierra.
  onCompleted: (summary: PayrollSubmissionSummary) => void;
}

// Color y clave i18n de la etiqueta por cada desenlace de corte.
type OutcomeMeta = { color: string; labelKey: string };

const OUTCOME_META: Record<PayrollSubmissionOutcome, OutcomeMeta> = {
  sent: { color: colors.feedback.success400, labelKey: 'attendance:monthlyCutsSendToPayroll.outcome.sent' },
  skipped_closed: { color: colors.feedback.alert400, labelKey: 'attendance:monthlyCutsSendToPayroll.outcome.skippedClosed' },
  error: { color: colors.feedback.error400, labelKey: 'attendance:monthlyCutsSendToPayroll.outcome.error' },
  not_found: { color: colors.grays.neutral66, labelKey: 'attendance:monthlyCutsSendToPayroll.outcome.notFound' },
};

// Fallback defensivo por si el backend enviara un desenlace no previsto: evita un acceso indefinido
// (labelKey vacío → se muestra el slug crudo del outcome en lugar de romper el render).
const OUTCOME_FALLBACK_META: OutcomeMeta = { color: colors.grays.neutral66, labelKey: '' };

export const SendMonthlyCutsToPayrollModal: React.FC<SendMonthlyCutsToPayrollModalProps> = ({
  isOpen,
  selectedCuts,
  onClose,
  onCompleted,
}) => {
  const { t: translate } = useTranslation();
  const [summary, setSummary] = useState<PayrollSubmissionSummary | null>(null);

  // Se envían todos los cortes seleccionados (incluidos los cerrados): el backend los reporta como
  // omitidos (skipped_closed) en el resultado, satisfaciendo el criterio de aceptación.
  const selectedCutIds = useMemo(() => selectedCuts.map((cut) => cut.id), [selectedCuts]);

  const isValid = useMemo(
    () => sendMonthlyCutsToPayrollSchema.safeParse({ monthlyCutIds: selectedCutIds }).success,
    [selectedCutIds],
  );

  // El diálogo pasa a la fase de resultado (fase 2) y delega los efectos posteriores al llamador.
  const handleSuccess = useCallback(
    (result: PayrollSubmissionSummary) => {
      setSummary(result);
      onCompleted(result);
    },
    [onCompleted],
  );

  const { isSubmitting, canSubmit, handleSubmit } = useSendToPayroll({
    selectedCutIds,
    onSuccess: handleSuccess,
  });

  // Al cerrar el diálogo se reinicia la fase para reabrir siempre en confirmación.
  useEffect(() => {
    if (!isOpen) setSummary(null);
  }, [isOpen]);

  const handleClose = useCallback(() => { onClose(); }, [onClose]);

  if (!isOpen) return null;

  // ── Fase 2: resultado por corte ────────────────────────────────────────────
  if (summary) {
    return (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
        data-testid="send-payroll-modal-backdrop"
      >
        <Card
          elevation={2}
          padding="24px"
          borderRadius="16px"
          data-testid="send-payroll-result-modal"
          className="flex flex-col gap-y-4 w-full max-w-[720px] max-h-[90vh] overflow-y-auto"
        >
          <Text variant="header-6" color={colors.primary.main} className="text-center">
            {translate('attendance:monthlyCutsSendToPayroll.result.title')}
          </Text>

          <Text variant="subheader-regular" color={colors.grays.neutral33}>
            {translate('attendance:monthlyCutsSendToPayroll.result.summary', {
              sent: summary.sentCount,
              skipped: summary.skippedClosedCount,
              errors: summary.errorCount,
              notFound: summary.notFoundCount,
            })}
          </Text>

          <div
            className="border rounded-2xl overflow-hidden"
            style={{ borderColor: colors.primary.auxiliar }}
          >
            <div className="overflow-y-auto max-h-[320px]">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b" style={{ borderColor: colors.primary.auxiliar }}>
                    <th className="text-left px-4 py-2 font-medium text-black">
                      {translate('attendance:monthlyCutsSendToPayroll.table.id')}
                    </th>
                    <th className="text-left px-4 py-2 font-medium text-black">
                      {translate('attendance:monthlyCutsSendToPayroll.table.company')}
                    </th>
                    <th className="text-left px-4 py-2 font-medium text-black">
                      {translate('attendance:monthlyCutsSendToPayroll.table.outcome')}
                    </th>
                    <th className="text-left px-4 py-2 font-medium text-black">
                      {translate('attendance:monthlyCutsSendToPayroll.table.detail')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {summary.results.map((item, index) => {
                    const meta = (OUTCOME_META as Record<string, OutcomeMeta>)[item.outcome]
                      ?? OUTCOME_FALLBACK_META;
                    // Detalle amigable por desenlace; el técnico del error va solo a la bitácora.
                    const detailText =
                      item.outcome === 'sent'
                        ? (item.conceptCount != null
                          ? translate('attendance:monthlyCutsSendToPayroll.result.conceptCount', {
                            count: item.conceptCount,
                          })
                          : '-')
                        : item.outcome === 'error'
                          ? translate('attendance:monthlyCutsSendToPayroll.result.errorHint')
                          : item.outcome === 'skipped_closed'
                            ? translate('attendance:monthlyCutsSendToPayroll.result.skippedClosedHint')
                            : item.outcome === 'not_found'
                              ? translate('attendance:monthlyCutsSendToPayroll.result.notFoundHint')
                              : '-';
                    return (
                      <tr
                        key={item.monthlyCutId}
                        style={{
                          backgroundColor:
                            index % 2 === 0 ? colors.grays.neutralFA : colors.grays.neutralFF,
                        }}
                        data-testid={`send-payroll-result-row-${item.monthlyCutId}`}
                      >
                        <td className="px-4 py-2" style={{ color: colors.grays.neutral66 }}>
                          {item.monthlyCutId}
                        </td>
                        <td className="px-4 py-2" style={{ color: colors.grays.neutral66 }}>
                          {item.companyName ?? '-'}
                        </td>
                        <td className="px-4 py-2 font-medium" style={{ color: meta.color }}>
                          {meta.labelKey ? translate(meta.labelKey) : item.outcome}
                        </td>
                        <td className="px-4 py-2" style={{ color: colors.grays.neutral66 }}>
                          {detailText}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex justify-center">
            <PrimaryButton
              onClick={handleClose}
              data-testid="send-payroll-result-close-button"
            >
              {translate('common:actions.close')}
            </PrimaryButton>
          </div>
        </Card>
      </div>
    );
  }

  // ── Fase 1: confirmación ────────────────────────────────────────────────────
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      data-testid="send-payroll-modal-backdrop"
    >
      <ConfirmModal
        variant="default"
        title={translate('attendance:monthlyCutsSendToPayroll.confirm.title')}
        bodyText={translate('attendance:monthlyCutsSendToPayroll.confirm.body', {
          count: selectedCuts.length,
        })}
        onConfirm={handleSubmit}
        onCancel={handleClose}
        confirmText={translate('attendance:monthlyCutsSendToPayroll.confirm.action')}
        cancelText={translate('common:actions.cancel')}
        isLoading={isSubmitting}
        isConfirmDisabled={!canSubmit || !isValid}
        cardClassName="w-full max-w-[720px] max-h-[90vh] overflow-y-auto"
      >
        <div
          className="border rounded-2xl overflow-hidden"
          style={{ borderColor: colors.primary.auxiliar }}
        >
          <div className="overflow-y-auto max-h-[280px]">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b" style={{ borderColor: colors.primary.auxiliar }}>
                  <th className="text-left px-4 py-2 font-medium text-black">
                    {translate('attendance:monthlyCutsSendToPayroll.table.id')}
                  </th>
                  <th className="text-left px-4 py-2 font-medium text-black">
                    {translate('attendance:monthlyCutsSendToPayroll.table.company')}
                  </th>
                  <th className="text-left px-4 py-2 font-medium text-black">
                    {translate('attendance:monthlyCutsSendToPayroll.table.period')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {selectedCuts.map((cut, index) => (
                  <tr
                    key={cut.id}
                    style={{
                      backgroundColor:
                        index % 2 === 0 ? colors.grays.neutralFA : colors.grays.neutralFF,
                    }}
                    data-testid={`send-payroll-cut-row-${cut.id}`}
                  >
                    <td className="px-4 py-2" style={{ color: colors.grays.neutral66 }}>
                      {cut.id}
                    </td>
                    <td className="px-4 py-2" style={{ color: colors.grays.neutral66 }}>
                      {cut.companyName}
                    </td>
                    <td className="px-4 py-2" style={{ color: colors.grays.neutral66 }}>
                      {`${cut.month} ${cut.year}`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </ConfirmModal>
    </div>
  );
};

export default SendMonthlyCutsToPayrollModal;
